import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { FinanceAnalyticsByMonthsResponseDto } from '@/features/finances/types/finances-dto';
import { useFinancesPageFilter } from '@/features/finances/store/useFinancesPageFilter';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth.ts';
import { useCurrencyStore } from '@/store/useCurrencyStore.ts';

type Props = {
    outerApartmentIds?: string[];
    ownerId?: string;
};

export const useFinancesAnalyticsByMonthsApi = ({ outerApartmentIds, ownerId }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useFinancesPageFilter();
    const { startDate, endDate, apartmentId } = filter;
    const { currentCurrency } = useCurrencyStore();
    const apartmentIds = apartmentId?.map((apartment) => apartment.id);

    const {
        data: analyticsByMonthsData,
        isLoading: isLoadingAnalyticsByMonthsData,
        isError: isErrorAnalyticsByMonthsData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.finances.queryKeys.analyticsMonthly,
            startDate,
            endDate,
            apartmentIds,
            ownerId,
            currentCurrency,
        ],
        queryFn: async (): Promise<FinanceAnalyticsByMonthsResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.features.finances.endpoints.analyticsMonthly,
                {
                    startDate: DateTimeHelper.getDateOnly(startDate),
                    endDate: DateTimeHelper.getDateOnly(endDate),
                    apartmentId: outerApartmentIds || apartmentIds,
                    ownerId
                },
            );
            const response = await axiosInstance.get<FinanceAnalyticsByMonthsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    return {
        analyticsByMonthsData,
        isLoadingAnalyticsByMonthsData,
        isErrorAnalyticsByMonthsData,
    };
};
